import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import {
  Lock, KeyRound, Eye, EyeOff, Shield, Users, ChevronRight,
  CheckCircle2, AlertTriangle, RefreshCw, UserCircle, Building2,
  Globe, Calendar, ArrowLeft, Save, Fingerprint, UserPlus
} from 'lucide-react';
import { hasPhpBackend, getApiBase } from '@/lib/phpApi';
import PageHeader from '@/components/PageHeader';

interface DbUser {
  id: number;
  username: string;
  name: string;
  email: string;
  role: string;
  country: string;
  company_name: string;
  created_at: string;
  last_login: string | null;
}

export default function PasswordResetPage() {
  const { user, isAuthenticated } = useAuth();
  const isAdmin = user?.role === 'admin';
  const phpAvailable = hasPhpBackend();

  // ─── Tab State ────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<'change' | 'admin' | 'create'>('change');

  // ─── Self-Service Change State ──────────────────────────
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [changing, setChanging] = useState(false);

  // ─── Admin State ────────────────────────────────────────
  const [dbUsers, setDbUsers] = useState<DbUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [resetUserId, setResetUserId] = useState<number | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [showResetPw, setShowResetPw] = useState(false);
  const [resetting, setResetting] = useState(false);
  // ─── Admin Create User State ────────────────────────────
  const [newUsername, setNewUsername] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState<'admin' | 'user'>('user');
  const [creatingUser, setCreatingUser] = useState(false);

  // ─── Admin Create User ──────────────────────────────────
  const handleCreateUser = async () => {
    if (!newUsername || !newUserPassword) {
      toast.error('Username and password are required');
      return;
    }
    if (newUserPassword.length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }

    setCreatingUser(true);
    try {
      // If PHP backend unavailable, use localStorage fallback
      if (!phpAvailable) {
        const MOCK_USERS_KEY = 'rb_mock_users';
        type MockUser = { username: string; password: string; user: { id: string; username: string; name: string; role: string; createdAt: string; }; };
        const loadMockUsers = (): MockUser[] => { try { const raw = localStorage.getItem(MOCK_USERS_KEY); if (raw) return JSON.parse(raw); } catch {} return []; };
        const saveMockUsers = (users: MockUser[]) => { try { localStorage.setItem(MOCK_USERS_KEY, JSON.stringify(users)); } catch {} };
        const users = loadMockUsers();
        if (users.some(u => u.username === newUsername)) {
          toast.error('Username already taken');
          setCreatingUser(false);
          return;
        }
        const newUser: MockUser = {
          username: newUsername,
          password: newUserPassword,
          user: {
            id: 'mock_' + Math.random().toString(36).slice(2, 10),
            username: newUsername,
            name: newUserName || newUsername,
            role: newUserRole,
            createdAt: new Date().toISOString(),
          },
        };
        users.push(newUser);
        saveMockUsers(users);
        toast.success(`User '${newUsername}' created (localStorage mode)`);
        setNewUsername('');
        setNewUserPassword('');
        setNewUserName('');
        setNewUserEmail('');
        setNewUserRole('user');
        // Reload local users list
        setDbUsers(users.map(u => ({
          id: parseInt(u.user.id.replace('mock_', '')) || Math.floor(Math.random() * 100000),
          username: u.user.username,
          name: u.user.name,
          email: '',
          role: u.user.role,
          country: '',
          company_name: '',
          created_at: u.user.createdAt,
          last_login: null,
        })));
        setCreatingUser(false);
        return;
      }

      const token = localStorage.getItem('rb_api_token');
      const base = localStorage.getItem('rb_api_url') || '/api';
      const url = base + '/auth/password.php' + (token ? `?_token=${encodeURIComponent(token)}` : '');

      console.log('[CreateUser] Sending to:', url);

      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          action: 'create',
          username: newUsername,
          password: newUserPassword,
          name: newUserName,
          email: newUserEmail,
          role: newUserRole,
          __token: token,
        }),
      });
      const text = await res.text();
      console.log('[CreateUser] Raw response:', text.substring(0, 500));

      // Check if server returned HTML instead of JSON (routing issue)
      if (text.trimStart().startsWith('<!DOCTYPE') || text.trimStart().startsWith('<!doctype') || text.trimStart().startsWith('<html')) {
        console.error('[CreateUser] Server returned HTML instead of JSON — falling back to localStorage mode');
        // FALLBACK: create user in localStorage since PHP routing is broken
        const MOCK_USERS_KEY = 'rb_mock_users';
        type MockUser = { username: string; password: string; user: { id: string; username: string; name: string; role: string; createdAt: string; }; };
        const loadMockUsers = (): MockUser[] => { try { const raw = localStorage.getItem(MOCK_USERS_KEY); if (raw) return JSON.parse(raw); } catch {} return []; };
        const saveMockUsers = (users: MockUser[]) => { try { localStorage.setItem(MOCK_USERS_KEY, JSON.stringify(users)); } catch {} };
        const users = loadMockUsers();
        if (users.some(u => u.username === newUsername)) {
          toast.error('Username already taken');
          setCreatingUser(false);
          return;
        }
        const newUser: MockUser = {
          username: newUsername,
          password: newUserPassword,
          user: {
            id: 'mock_' + Math.random().toString(36).slice(2, 10),
            username: newUsername,
            name: newUserName || newUsername,
            role: newUserRole,
            createdAt: new Date().toISOString(),
          },
        };
        users.push(newUser);
        saveMockUsers(users);
        toast.success(`User '${newUsername}' created (localStorage mode)`);
        setNewUsername('');
        setNewUserPassword('');
        setNewUserName('');
        setNewUserEmail('');
        setNewUserRole('user');
        setDbUsers(users.map(u => ({
          id: parseInt(u.user.id.replace('mock_', '')) || Math.floor(Math.random() * 100000),
          username: u.user.username,
          name: u.user.name,
          email: '',
          role: u.user.role,
          country: '',
          company_name: '',
          created_at: u.user.createdAt,
          last_login: null,
        })));
        setCreatingUser(false);
        return;
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch {
        console.error('Server returned non-JSON:', text.substring(0, 200));
        toast.error('Server error: ' + text.substring(0, 100));
        return;
      }
      if (data.success) {
        toast.success(`User '${newUsername}' created`);
        setNewUsername('');
        setNewUserPassword('');
        setNewUserName('');
        setNewUserEmail('');
        setNewUserRole('user');
        loadUsers();
      } else {
        toast.error(data.error || 'Failed to create user');
      }
    } catch (err) {
      toast.error('Network error: ' + (err instanceof Error ? err.message : 'Failed'));
    } finally {
      setCreatingUser(false);
    }
  };

  // ─── Load users (admin only) ────────────────────────────
  const loadUsers = useCallback(async () => {
    if (!isAdmin) return;
    setLoadingUsers(true);
    try {
      if (!phpAvailable) {
        // Load from localStorage mock users
        const MOCK_USERS_KEY = 'rb_mock_users';
        type MockUser = { username: string; password: string; user: { id: string; username: string; name: string; role: string; createdAt: string; }; };
        const loadMockUsers = (): MockUser[] => { try { const raw = localStorage.getItem(MOCK_USERS_KEY); if (raw) return JSON.parse(raw); } catch {} return []; };
        const users = loadMockUsers();
        setDbUsers(users.map(u => ({
          id: parseInt(u.user.id.replace('mock_', '')) || Math.floor(Math.random() * 100000),
          username: u.user.username,
          name: u.user.name,
          email: '',
          role: u.user.role,
          country: '',
          company_name: '',
          created_at: u.user.createdAt,
          last_login: null,
        })));
        return;
      }
      const token = localStorage.getItem('rb_api_token');
      const res = await fetch(`${getApiBase()}/auth/password.php` + (token ? `?_token=${encodeURIComponent(token)}` : ''), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({ action: 'list_users', __token: token }),
      });
      const data = await res.json();
      if (data.users) {
        setDbUsers(data.users);
      } else if (data.error) {
        toast.error(data.error);
      }
    } catch (err) {
      toast.error('Failed to load users: ' + (err instanceof Error ? err.message : 'Network error'));
    } finally {
      setLoadingUsers(false);
    }
  }, [isAdmin, phpAvailable]);

  useEffect(() => {
    if (activeTab === 'admin' && isAdmin) {
      loadUsers();
    }
  }, [activeTab, isAdmin, loadUsers]);

  // ─── Change Own Password ────────────────────────────────
  const handleChangePassword = async () => {
    if (!newPw || !confirmPw || !currentPw) {
      toast.error('All fields are required');
      return;
    }
    if (newPw !== confirmPw) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPw.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }

    setChanging(true);
    try {
      const token = localStorage.getItem('rb_api_token');
      const res = await fetch(`${getApiBase()}/auth/password.php` + (token ? `?_token=${encodeURIComponent(token)}` : ''), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          action: 'change',
          current_password: currentPw,
          new_password: newPw,
          __token: token,
        }),
      });
      const text = await res.text();
      if (text.trimStart().startsWith('<!DOCTYPE') || text.trimStart().startsWith('<!doctype') || text.trimStart().startsWith('<html')) {
        toast.error('Server routing error - upload .htaccess to server root');
        return;
      }
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        console.error('Server returned non-JSON:', text.substring(0, 200));
        toast.error('Server error: ' + text.substring(0, 100));
        return;
      }
      if (data.success) {
        toast.success('Password changed successfully');
        setCurrentPw('');
        setNewPw('');
        setConfirmPw('');
      } else {
        toast.error(data.error || 'Failed to change password');
      }
    } catch (err) {
      toast.error('Network error: ' + (err instanceof Error ? err.message : 'Failed'));
    } finally {
      setChanging(false);
    }
  };

  // ─── Admin Reset Password ───────────────────────────────
  const handleResetPassword = async (targetUserId: number) => {
    if (!resetPassword || resetPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setResetting(true);
    try {
      const token = localStorage.getItem('rb_api_token');
      const res = await fetch(`${getApiBase()}/auth/password.php` + (token ? `?_token=${encodeURIComponent(token)}` : ''), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          action: 'reset',
          user_id: targetUserId,
          new_password: resetPassword,
          __token: token,
        }),
      });
      const text = await res.text();
      if (text.trimStart().startsWith('<!DOCTYPE') || text.trimStart().startsWith('<!doctype') || text.trimStart().startsWith('<html')) {
        toast.error('Server routing error - upload .htaccess to server root');
        return;
      }
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        console.error('Server returned non-JSON:', text.substring(0, 200));
        toast.error('Server error: ' + text.substring(0, 100));
        return;
      }
      if (data.success) {
        toast.success(`Password reset for ${data.username}`);
        setResetUserId(null);
        setResetPassword('');
        loadUsers();
      } else {
        toast.error(data.error || 'Failed to reset password');
      }
    } catch (err) {
      toast.error('Network error: ' + (err instanceof Error ? err.message : 'Failed'));
    } finally {
      setResetting(false);
    }
  };

  // ─── Strength Indicator ─────────────────────────────────
  const getStrength = (pw: string): { label: string; color: string; width: number } => {
    if (!pw) return { label: '', color: 'bg-slate-700', width: 0 };
    let score = 0;
    if (pw.length >= 8) score++;
    if (pw.length >= 12) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;

    if (score <= 1) return { label: 'Weak', color: 'bg-red-500', width: 20 };
    if (score <= 2) return { label: 'Fair', color: 'bg-amber-500', width: 40 };
    if (score <= 3) return { label: 'Good', color: 'bg-yellow-400', width: 60 };
    if (score <= 4) return { label: 'Strong', color: 'bg-emerald-400', width: 80 };
    return { label: 'Excellent', color: 'bg-emerald-500', width: 100 };
  };
  const strength = getStrength(newPw);

  if (!phpAvailable) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Shield className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-300 mb-2">Server Offline</h2>
          <p className="text-slate-500 max-w-sm">Password management requires a connection to the PHP backend server.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <PageHeader
        icon={KeyRound}
        iconColor="text-amber-400"
        title="Password Management"
        subtitle="Change your password or manage user credentials"
      />

      {/* ═════ TABS ═════ */}
      <div className="flex gap-1 border-b border-slate-800/60">
        <button
          onClick={() => setActiveTab('change')}
          className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold transition-all border-b-2 -mb-px ${
            activeTab === 'change'
              ? 'text-amber-400 border-amber-400'
              : 'text-slate-500 border-transparent hover:text-slate-300'
          }`}
        >
          <Lock className="w-3.5 h-3.5" />
          Change Password
        </button>
        {isAdmin && (
          <button
            onClick={() => setActiveTab('create')}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs font-bold transition-all border-b-2 -mb-px ${
              activeTab === 'create'
                ? 'text-amber-400 border-amber-400'
                : 'text-slate-500 border-transparent hover:text-slate-300'
            }`}
          >
            <UserPlus className="w-3.5 h-3.5" />
            Create User
          </button>
        )}
      </div>

      <AnimatePresence mode="wait">
        {/* ═════ TAB: Change Password ═════ */}
        {activeTab === 'change' && (
          <motion.div
            key="change"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="grid grid-cols-1 lg:grid-cols-3 gap-6"
          >
            {/* Form */}
            <div className="lg:col-span-2 space-y-4">
              <div className="p-6 space-y-5 rb-inner-card">
                <div className="flex items-center gap-3 mb-2">
                  <Fingerprint className="w-5 h-5 text-amber-400" />
                  <h3 className="text-lg font-semibold text-slate-200">Change Your Password</h3>
                </div>

                {/* Current Password */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-400">Current Password</label>
                  <div className="relative">
                    <input
                      type={showCurrent ? 'text' : 'password'}
                      value={currentPw}
                      onChange={(e) => setCurrentPw(e.target.value)}
                      className="w-full px-4 py-3 text-slate-200 pr-12 focus:border-amber-500/50 focus:outline-none transition-colors rb-card-sm"
                      placeholder="Enter your current password"
                    />
                    <button
                      onClick={() => setShowCurrent(!showCurrent)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    >
                      {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-400">New Password</label>
                  <div className="relative">
                    <input
                      type={showNew ? 'text' : 'password'}
                      value={newPw}
                      onChange={(e) => setNewPw(e.target.value)}
                      className="w-full px-4 py-3 text-slate-200 pr-12 focus:border-amber-500/50 focus:outline-none transition-colors rb-card-sm"
                      placeholder="Min 6 characters"
                    />
                    <button
                      onClick={() => setShowNew(!showNew)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    >
                      {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {/* Strength bar */}
                  {newPw && (
                    <div className="space-y-1">
                      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${strength.width}%` }}
                          className={`h-full ${strength.color} rounded-full`}
                          transition={{ duration: 0.3 }}
                        />
                      </div>
                      <p className={`text-xs ${strength.width >= 60 ? 'text-emerald-400' : strength.width >= 40 ? 'text-amber-400' : 'text-red-400'}`}>
                        {strength.label}
                      </p>
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-400">Confirm New Password</label>
                  <div className="relative">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      value={confirmPw}
                      onChange={(e) => setConfirmPw(e.target.value)}
                      className={`w-full bg-rb-surface border rounded-lg px-4 py-3 text-slate-200 pr-12 focus:outline-none transition-colors ${
                        confirmPw && confirmPw !== newPw
                          ? 'border-red-500/50 focus:border-red-500'
                          : 'border-slate-700 focus:border-amber-500/50'
                      }`}
                      placeholder="Re-enter new password"
                    />
                    <button
                      onClick={() => setShowConfirm(!showConfirm)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    >
                      {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {confirmPw && confirmPw !== newPw && (
                    <p className="text-xs text-red-400 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Passwords do not match
                    </p>
                  )}
                  {confirmPw && confirmPw === newPw && newPw.length >= 6 && (
                    <p className="text-xs text-emerald-400 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Passwords match
                    </p>
                  )}
                </div>

                {/* Submit */}
                <button
                  onClick={handleChangePassword}
                  disabled={changing || !currentPw || !newPw || newPw !== confirmPw || newPw.length < 6}
                  className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:bg-slate-700 disabled:text-slate-500 text-slate-200 font-semibold py-3 rounded-lg transition-all"
                >
                  {changing ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {changing ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </div>

            {/* Info Panel */}
            <div className="space-y-4">
              <div className="p-5 rb-inner-card">
                <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-amber-400" />
                  Password Requirements
                </h4>
                <ul className="space-y-2 text-sm text-slate-400">
                  <li className="flex items-center gap-2">
                    <ChevronRight className="w-3 h-3 text-slate-600" />
                    Minimum 6 characters
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="w-3 h-3 text-slate-600" />
                    Mix of letters and numbers recommended
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="w-3 h-3 text-slate-600" />
                    Uppercase & lowercase for stronger security
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="w-3 h-3 text-slate-600" />
                    Special characters add extra protection
                  </li>
                </ul>
              </div>

              <div className="p-5 rb-inner-card">
                <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                  <UserCircle className="w-4 h-4 text-amber-400" />
                  Current User
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Username</span>
                    <span className="text-slate-200 font-medium">{user?.username || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Role</span>
                    <span className={`font-medium ${isAdmin ? 'text-amber-400' : 'text-slate-200'}`}>
                      {user?.role === 'admin' ? 'Administrator' : 'User'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Name</span>
                    <span className="text-slate-200">{user?.name || '—'}</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ═════ TAB: Admin User Management ═════ */}
        {activeTab === 'admin' && isAdmin && (
          <motion.div
            key="admin"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4"
          >
            <div className="overflow-hidden rb-inner-card">
              <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-amber-400" />
                  <h3 className="text-lg font-semibold text-slate-200">User Management</h3>
                </div>
                <button
                  onClick={loadUsers}
                  disabled={loadingUsers}
                  className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
                >
                  <RefreshCw className={`w-3 h-3 ${loadingUsers ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>

              {loadingUsers ? (
                <div className="flex items-center justify-center py-16">
                  <RefreshCw className="w-6 h-6 text-amber-400 animate-spin" />
                </div>
              ) : dbUsers.length === 0 ? (
                <div className="text-center py-16 text-slate-500">
                  <Users className="w-10 h-10 mx-auto mb-3 text-slate-400" />
                  <p>No users found in database</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-800">
                        <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">User</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Role</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Company</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Country</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Last Login</th>
                        <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/50">
                      {dbUsers.map((u) => (
                        <tr key={u.id} className="hover:bg-slate-800/20 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                                <UserCircle className="w-4 h-4 text-amber-400" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-200">{u.name || u.username}</p>
                                <p className="text-xs text-slate-500">{u.email || u.username}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                              u.role === 'admin'
                                ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                                : 'bg-slate-700/30 text-slate-400 border border-slate-600/20'
                            }`}>
                              {u.role === 'admin' ? <Shield className="w-3 h-3" /> : <UserCircle className="w-3 h-3" />}
                              {u.role}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-400">
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3 text-slate-600" />
                              {u.company_name || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-400">
                            <span className="flex items-center gap-1">
                              <Globe className="w-3 h-3 text-slate-600" />
                              {u.country || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-400">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3 text-slate-600" />
                              {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {resetUserId === u.id ? (
                              <div className="flex items-center gap-2">
                                <div className="relative flex-1 max-w-[200px]">
                                  <input
                                    type={showResetPw ? 'text' : 'password'}
                                    value={resetPassword}
                                    onChange={(e) => setResetPassword(e.target.value)}
                                    className="w-full px-3 py-2 text-sm text-slate-200 pr-8 focus:border-amber-500/50 focus:outline-none rb-card-sm"
                                    placeholder="New password"
                                    autoFocus
                                  />
                                  <button
                                    onClick={() => setShowResetPw(!showResetPw)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500"
                                  >
                                    {showResetPw ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                  </button>
                                </div>
                                <button
                                  onClick={() => handleResetPassword(u.id)}
                                  disabled={resetting || resetPassword.length < 6}
                                  className="px-3 py-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 rounded-lg text-xs font-medium hover:bg-emerald-500/20 disabled:opacity-50 transition-colors"
                                >
                                  {resetting ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Save'}
                                </button>
                                <button
                                  onClick={() => { setResetUserId(null); setResetPassword(''); }}
                                  className="px-3 py-2 bg-slate-800 text-slate-400 rounded-lg text-xs font-medium hover:bg-slate-700 transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setResetUserId(u.id)}
                                className="flex items-center gap-1.5 px-3 py-2 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-lg text-xs font-medium hover:bg-amber-500/20 transition-colors"
                              >
                                <KeyRound className="w-3 h-3" />
                                Reset Password
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </motion.div>
        )}
        {/* ═════ TAB: Create User ═════ */}
        {activeTab === 'create' && isAdmin && (
          <motion.div
            key="create"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="max-w-lg mx-auto"
          >
            <div className="p-6 space-y-5 rb-inner-card">
              <div className="flex items-center gap-3">
                <UserPlus className="w-5 h-5 text-amber-400" />
                <h3 className="text-lg font-semibold text-slate-200">Create New User</h3>
              </div>
              <p className="text-xs text-slate-500">
                Create a new user account. The user will be able to log in immediately with the credentials you set.
              </p>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-400">Username *</label>
                  <input
                    type="text"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    className="w-full px-4 py-3 text-slate-200 focus:border-amber-500/50 focus:outline-none rb-card-sm"
                    placeholder="e.g. john.doe"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-400">Password *</label>
                  <input
                    type="text"
                    value={newUserPassword}
                    onChange={(e) => setNewUserPassword(e.target.value)}
                    className="w-full px-4 py-3 text-slate-200 focus:border-amber-500/50 focus:outline-none rb-card-sm"
                    placeholder="Min 4 characters"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-400">Full Name</label>
                  <input
                    type="text"
                    value={newUserName}
                    onChange={(e) => setNewUserName(e.target.value)}
                    className="w-full px-4 py-3 text-slate-200 focus:border-amber-500/50 focus:outline-none rb-card-sm"
                    placeholder="e.g. John Doe"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-400">Email</label>
                  <input
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    className="w-full px-4 py-3 text-slate-200 focus:border-amber-500/50 focus:outline-none rb-card-sm"
                    placeholder="e.g. john@company.com"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-400">Role</label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setNewUserRole('user')}
                      className={`flex-1 py-2.5 rounded-lg border text-xs font-medium transition-all ${
                        newUserRole === 'user'
                          ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                          : 'bg-rb-surface border-slate-700 text-slate-500 hover:text-slate-300'
                      }`}
                    >
                      <UserCircle className="w-3.5 h-3.5 mx-auto mb-1" />
                      Standard User
                    </button>
                    <button
                      onClick={() => setNewUserRole('admin')}
                      className={`flex-1 py-2.5 rounded-lg border text-xs font-medium transition-all ${
                        newUserRole === 'admin'
                          ? 'bg-amber-500/10 border-amber-500/30 text-amber-400'
                          : 'bg-rb-surface border-slate-700 text-slate-500 hover:text-slate-300'
                      }`}
                    >
                      <Shield className="w-3.5 h-3.5 mx-auto mb-1" />
                      Administrator
                    </button>
                  </div>
                </div>

                <button
                  onClick={handleCreateUser}
                  disabled={creatingUser || !newUsername || !newUserPassword}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-amber-500 hover:bg-amber-400 disabled:bg-slate-700 disabled:text-slate-500 text-slate-200 font-semibold rounded-lg transition-all"
                >
                  {creatingUser ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <UserPlus className="w-4 h-4" />
                  )}
                  {creatingUser ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
